import { useQuery } from "@apollo/client";
import { alpha, Box, Button, Paper, Skeleton, Stack, Typography } from "@mui/material";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import React from "react";
import { BUSINESS_CITY_FILTER_AUTOCOMPLETE, BUSINESS_COUNTRY_FILTER_AUTOCOMPLETE, BUSINESS_STATE_FILTER_AUTOCOMPLETE } from "../businesses/businesses.queries";
import { FilterAutocomplete } from "../businesses/BusinessFilters";
import { FilterAutocomplete as UserFilterAutocomplete } from "../users/UserFilters";
import { CITY_FILTER_AUTOCOMPLETE, COUNTRY_FILTER_AUTOCOMPLETE, STATE_FILTER_AUTOCOMPLETE } from "../users/users.queries";
import { downloadCsv, localDate, toCsv } from "./csv";
import { DateRangeField } from "./DateRangeField";
import { formatCount, shareOf } from "./reportFormat";
import { NO_REGION, Region, RegionOption, ReportParams } from "./reportParams";
import { ReportError } from "./reportParts";
import { JOINED_TEAMS_COUNT, RECOMMENDED_BUSINESSES_COUNT, RECOMMENDED_UNIQUE_BUSINESSES_COUNT, RECOMMENDER_COUNT, REFERRED_USER_COUNT } from "./reports.queries";

type Option = RegionOption;
type Totals = { total: number; us: number; dmv: number; zimbabwe: number; filter: number };

const regionVariables = (region: Region) => ({
  regionCountry: region.country?.name || null,
  regionState: region.state?.name || null,
  regionCity: region.city?.name || null,
});

/** The narrowest place picked, for the "Selected region" card's label. */
const regionName = (region: Region) => region.city?.name || region.state?.name || region.country?.name || null;

// Null shows an empty box; the autocompletes hand back an empty option when cleared.
const asValue = (option: Option) => option as { name: string; displayName: string };
const orNull = (option: { name: string; displayName: string }): Option => (option?.name ? option : null);

// ---- Cards ---------------------------------------------------------------

const CardShell: React.FC<{ accent?: boolean; muted?: boolean; children: React.ReactNode }> = ({ accent, muted, children }) => (
  <Paper
    variant="outlined"
    sx={(theme) => ({
      p: 2,
      borderRadius: 3,
      minHeight: 116,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      borderStyle: muted ? "dashed" : "solid",
      bgcolor: accent ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
      borderColor: accent ? alpha(theme.palette.primary.main, 0.35) : "divider",
      transition: "box-shadow 120ms, border-color 120ms",
      "&:hover": muted ? undefined : { boxShadow: 2 },
    })}
  >
    {children}
  </Paper>
);

const CardLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary" }}
    noWrap
  >
    {children}
  </Typography>
);

/** One number in a row: its label, the value, and its share of the row's total. */
const NumberCard: React.FC<{
  label: string;
  value?: number;
  total?: number;
  loading: boolean;
  accent?: boolean;
}> = ({ label, value, total, loading, accent }) => {
  const share = !accent && total && value !== undefined ? shareOf(value, total) : null;
  return (
    <CardShell accent={accent}>
      <CardLabel>{label}</CardLabel>
      {loading || value === undefined ? (
        <Skeleton width={70} height={44} />
      ) : (
        <Typography
          sx={{
            fontSize: accent ? 36 : 32,
            fontWeight: 700,
            lineHeight: 1.15,
            fontVariantNumeric: "tabular-nums",
            color: accent ? "primary.main" : "text.primary",
          }}
        >
          {formatCount(value)}
        </Typography>
      )}
      <Box sx={{ minHeight: 18 }}>
        {accent && (
          <Typography variant="caption" color="text.secondary">
            Everywhere
          </Typography>
        )}
        {share !== null && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box sx={{ width: `${share.width}%`, height: "100%", bgcolor: "primary.main", borderRadius: 2 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums", minWidth: 30, textAlign: "right" }}>
              {share.label}
            </Typography>
          </Box>
        )}
      </Box>
    </CardShell>
  );
};

/** The fifth card: whatever region is picked above, or an invitation to pick one. */
const SelectedRegionCard: React.FC<{ region: string | null; value?: number; total?: number; loading: boolean }> = ({
  region,
  value,
  total,
  loading,
}) =>
  region ? (
    <NumberCard label={`Selected · ${region}`} value={value} total={total} loading={loading} />
  ) : (
    <CardShell muted>
      <CardLabel>Selected region</CardLabel>
      <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.15, color: "text.disabled" }}>—</Typography>
      <Typography variant="caption" color="text.secondary">
        Pick a region in the parameters
      </Typography>
    </CardShell>
  );

/** One measure: its name, what it counts, and five cards across. */
const MeasureRow: React.FC<{
  title: string;
  hint: string;
  values?: Totals;
  loading: boolean;
  region: string | null;
}> = ({ title, hint, values, loading, region }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1.25, flexWrap: "wrap" }}>
      <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    </Box>
    <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
      <NumberCard label="Total" value={values?.total} loading={loading} accent />
      <NumberCard label="United States" value={values?.us} total={values?.total} loading={loading} />
      <NumberCard label="DMV" value={values?.dmv} total={values?.total} loading={loading} />
      <NumberCard label="Zimbabwe" value={values?.zimbabwe} total={values?.total} loading={loading} />
      <SelectedRegionCard region={region} value={values?.filter} total={values?.total} loading={loading} />
    </Box>
  </Box>
);

const Group: React.FC<{ title: string; note: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
      {note}
    </Typography>
    <Stack spacing={3}>{children}</Stack>
  </Paper>
);

// ---- Filters -------------------------------------------------------------

/**
 * One labelled row of the filter panel. `display: contents` puts its cells
 * straight into the panel's grid, so every row's boxes line up in columns.
 */
const FilterRow: React.FC<{ label: string; trailing?: React.ReactNode; children: React.ReactNode }> = ({
  label,
  trailing,
  children,
}) => (
  <Box sx={{ display: "contents" }}>
    <Box sx={{ alignSelf: "center", pt: { xs: 1, md: 0 } }}>
      <CardLabel>{label}</CardLabel>
    </Box>
    {children}
    <Box sx={{ alignSelf: "center", display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>{trailing}</Box>
  </Box>
);

const clearButton = (onClear: () => void) => (
  <Button size="small" onClick={onClear}>
    Clear
  </Button>
);

/** The Dates, Members from and Businesses in rows of this report's parameters. */
export const TotalsParams: React.FC<{
  value: ReportParams["totals"];
  onChange: (next: ReportParams["totals"]) => void;
}> = ({ value, onChange }) => {
  const { people, places } = value;
  const setPeople = (next: Region) => onChange({ ...value, people: next });
  const setPlaces = (next: Region) => onChange({ ...value, places: next });

  return (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            columnGap: 2,
            alignItems: "center",
            gridTemplateColumns: { xs: "1fr", md: "130px repeat(3, minmax(0, 1fr)) 150px" },
          }}
        >
          <FilterRow label="Dates">
            <DateRangeField value={value.dates} onChange={(dates) => onChange({ ...value, dates })} allowAllTime label="" fullWidth />
            <Box sx={{ display: { xs: "none", md: "block" } }} />
            <Box sx={{ display: { xs: "none", md: "block" } }} />
          </FilterRow>

          <FilterRow label="Members from" trailing={regionName(people) ? clearButton(() => setPeople(NO_REGION)) : null}>
            <UserFilterAutocomplete
              query={COUNTRY_FILTER_AUTOCOMPLETE}
              label="Country"
              noOptionsText="No country"
              value={asValue(people.country)}
              optionExtractor={(data) =>
                data.getCountriesAutocomplete.map((country: any) => ({ displayName: country.regionCountry, name: country.regionCountry }))
              }
              onChange={(option) => setPeople({ ...people, country: orNull(option) })}
            />
            <UserFilterAutocomplete
              query={STATE_FILTER_AUTOCOMPLETE}
              label="State"
              noOptionsText="No state"
              value={asValue(people.state)}
              optionExtractor={(data) =>
                data.getStatesAutocomplete.map((state: any) => ({
                  name: state.regionState,
                  displayName: `${state.regionState}, ${state.regionCountry}`,
                }))
              }
              onChange={(option) => setPeople({ ...people, state: orNull(option) })}
            />
            <UserFilterAutocomplete
              query={CITY_FILTER_AUTOCOMPLETE}
              label="City"
              noOptionsText="No city"
              value={asValue(people.city)}
              optionExtractor={(data) =>
                data.getCitiesAutocomplete.map((city: any) => ({
                  name: city.regionCity,
                  displayName: `${city.regionCity}, ${city.regionState}, ${city.regionCountry}`,
                }))
              }
              onChange={(option) => setPeople({ ...people, city: orNull(option) })}
            />
          </FilterRow>

          <FilterRow label="Businesses in" trailing={regionName(places) ? clearButton(() => setPlaces(NO_REGION)) : null}>
            <FilterAutocomplete
              query={BUSINESS_COUNTRY_FILTER_AUTOCOMPLETE}
              label="Country"
              noOptionsText="No country"
              value={asValue(places.country)}
              optionExtractor={(data) =>
                data.getBusinessCountriesAutocomplete.map((country: any) => ({ displayName: country.country, name: country.country }))
              }
              onChange={(option) => setPlaces({ ...places, country: orNull(option) })}
            />
            <FilterAutocomplete
              query={BUSINESS_STATE_FILTER_AUTOCOMPLETE}
              label="State"
              noOptionsText="No state"
              value={asValue(places.state)}
              optionExtractor={(data) =>
                data.getBusinessStatesAutocomplete.map((state: any) => ({ name: state.state, displayName: `${state.state}, ${state.country}` }))
              }
              onChange={(option) => setPlaces({ ...places, state: orNull(option) })}
            />
            <FilterAutocomplete
              query={BUSINESS_CITY_FILTER_AUTOCOMPLETE}
              label="City"
              noOptionsText="No city"
              value={asValue(places.city)}
              optionExtractor={(data) =>
                data.getBusinessCitiesAutocomplete.map((city: any) => ({
                  name: city.city,
                  displayName: `${city.city}, ${city.state}, ${city.country}`,
                }))
              }
              onChange={(option) => setPlaces({ ...places, city: orNull(option) })}
            />
          </FilterRow>
        </Box>
  );
};

/**
 * Totals by region: the Reports page's original counts, each split by the
 * regions the team follows (US, DMV, Zimbabwe) and one region picked in the
 * parameter pane. Members are placed by their profile region, businesses by
 * their address.
 */
export const Reports: React.FC<ReportParams["totals"]> = ({ dates, people, places }) => {
  const range = { dateFrom: dates.from, dateTo: dates.to };
  const peopleVariables = { ...range, ...regionVariables(people) };
  const placesVariables = { ...range, ...regionVariables(places) };

  const referred = useQuery(REFERRED_USER_COUNT, { variables: peopleVariables, fetchPolicy: "cache-and-network" });
  const recommenders = useQuery(RECOMMENDER_COUNT, { variables: peopleVariables, fetchPolicy: "cache-and-network" });
  const joined = useQuery(JOINED_TEAMS_COUNT, { variables: peopleVariables, fetchPolicy: "cache-and-network" });
  const recommendations = useQuery(RECOMMENDED_BUSINESSES_COUNT, { variables: placesVariables, fetchPolicy: "cache-and-network" });
  const unique = useQuery(RECOMMENDED_UNIQUE_BUSINESSES_COUNT, { variables: placesVariables, fetchPolicy: "cache-and-network" });

  const error = referred.error ?? recommenders.error ?? joined.error ?? recommendations.error ?? unique.error;
  const peopleRegion = regionName(people);
  const placesRegion = regionName(places);

  // Each measure once: the cards and the CSV both read this list.
  const groups = [
    {
      title: "Members",
      note: "Placed by the region on each member's profile.",
      region: peopleRegion,
      measures: [
        { title: "People referred", hint: "Joined through someone's referral link", result: referred, values: referred.data?.reportReferredUsers },
        { title: "Recommenders", hint: "Members who recommended at least one place", result: recommenders, values: recommenders.data?.reportRecommenders },
        { title: "Joined a community", hint: "Members in at least one community", result: joined, values: joined.data?.reportJoinedTeams },
      ],
    },
    {
      title: "Businesses",
      note: "Placed by each business's address, not by the member who recommended it.",
      region: placesRegion,
      measures: [
        {
          title: "Recommendations",
          hint: "Every recommendation made",
          result: recommendations,
          values: recommendations.data?.reportBusinessRecommendations,
        },
        { title: "Businesses recommended", hint: "Each business counted once", result: unique, values: unique.data?.reportUniqueBusinessRecommendations },
      ],
    },
  ];
  const ready = groups.every((group) => group.measures.every((measure) => !measure.result.loading && measure.values));

  const exportCsv = () => {
    const from = dates.from ? localDate(dates.from) : "All time";
    const to = dates.to ? localDate(dates.to) : "";
    const rows = [
      ["Group", "Measure", "Total", "United States", "DMV", "Zimbabwe", "Selected region", "In selected region", "From", "To"],
      ...groups.flatMap((group) =>
        group.measures.map((measure) => {
          const values = measure.values as Totals;
          return [
            group.title,
            measure.title,
            values.total,
            values.us,
            values.dmv,
            values.zimbabwe,
            group.region ?? "",
            group.region ? values.filter : "",
            from,
            to,
          ];
        }),
      ),
    ];
    downloadCsv(`totals-by-region-${localDate(new Date())}.csv`, toCsv(rows));
  };

  return (
    <Stack spacing={2.5}>
      <ReportError error={error} />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" startIcon={<FileDownloadOutlined />} disabled={!ready} onClick={exportCsv}>
          Export CSV
        </Button>
      </Box>

      {groups.map((group) => (
        <Group key={group.title} title={group.title} note={group.note}>
          {group.measures.map((measure) => (
            <MeasureRow
              key={measure.title}
              title={measure.title}
              hint={measure.hint}
              values={measure.values}
              loading={measure.result.loading}
              region={group.region}
            />
          ))}
        </Group>
      ))}
    </Stack>
  );
};
